(() => {
  'use strict';
  const instances = new WeakMap();
  const pageSize = 4;

  function mount(root) {
    if (instances.has(root)) return;
    const abort = new AbortController();
    const options = { signal: abort.signal };
    const preview = /^\d+$/.test(root.dataset.previewThemeId || '') ? root.dataset.previewThemeId : '';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const grid = root.querySelector('[data-fsic-grid]');
    const fullCollection = Boolean(root.querySelector('[data-fsic-full-collection]'));
    let visibleCount = pageSize;
    let previousIds = [];
    let more;

    function storefrontURL(value) {
      if (!value || value.startsWith('#')) return value;
      try {
        const url = new URL(value, location.origin);
        if (url.origin !== location.origin) return value;
        if (preview) url.searchParams.set('preview_theme_id', preview);
        return url.href;
      } catch (_) { return value; }
    }
    function preservePreview(link) {
      const value = link.getAttribute('href');
      if (value) link.setAttribute('href', storefrontURL(value));
    }
    root.querySelectorAll('a[href]').forEach(preservePreview);

    function showCards() {
      if (!grid) return;
      const cards = [...grid.querySelectorAll(':scope > [data-fhg-card]')];
      let shown = 0;
      cards.forEach(card => { const matched = card.dataset.fsMySizeHidden !== 'true'; card.hidden = !matched || (!fullCollection && shown++ >= visibleCount); });
      if (more) more.hidden = cards.filter(card => card.dataset.fsMySizeHidden !== 'true').length <= visibleCount;
    }
    function enhanceCards() {
      if (!grid) return;
      const cards = [...grid.querySelectorAll(':scope > [data-fhg-card]')];
      const ids = cards.map(card => card.dataset.fhgCard);
      if (!previousIds.every((id, index) => ids[index] === id)) visibleCount = pageSize;
      previousIds = ids;
      cards.forEach(card => {
        const productLink = card.querySelector('a.fhg__tile');
        const title = card.querySelector('h3')?.textContent.trim();
        if (!productLink || !/^\d+$/.test(card.dataset.fhgCard || '') || !title) return;
        preservePreview(productLink);
        card.dataset.fhdCard = card.dataset.fhgCard;
        card.dataset.productId = card.dataset.fhgCard;
        card.querySelector('.fhg__price')?.setAttribute('data-fhd-price', '');
        const photo = card.querySelector('.fhg__photo img');
        if (photo) {
          photo.classList.add('fhd__front');
          photo.sizes = fullCollection ? '22vw' : '(min-width: 990px) 24vw, (min-width: 750px) 24vw, 47vw';
        }
        card.dataset.fsLiveCompactCard = card.dataset.fhgCard;
        card.querySelector('.fsb-card-quickview')?.remove();
        productLink.dataset.fsLiveQuickview = card.dataset.fhgCard;
        productLink.dataset.productUrl = productLink.href;
        productLink.dataset.productTitle = title;
        productLink.setAttribute('aria-haspopup', 'dialog');
        productLink.setAttribute('aria-label', `${root.dataset.quickViewLabel || ''}: ${title}`);
        if (photo?.getAttribute('src')) productLink.dataset.productImage = photo.getAttribute('src');
        const plus = card.querySelector('[data-fsic-plus]');
        if (plus) {
          plus.dataset.fsQuickviewPurchase = '';
          plus.dataset.fsLiveQuickview = card.dataset.fhgCard;
          plus.dataset.productUrl = productLink.href;
          plus.dataset.productTitle = title;
          if (photo?.getAttribute('src')) plus.dataset.productImage = photo.getAttribute('src');
          plus.hidden = false;
        }
      });
      showCards();
    }
    root.addEventListener('fs:my-size:applied', showCards, options);
    if (grid && !fullCollection) {
      more = document.createElement('button');
      more.type = 'button';
      more.className = 'fsb-more-products';
      more.textContent = root.dataset.moreProductsLabel || '';
      more.setAttribute('aria-controls', grid.closest('[data-fsic-panel]')?.id || '');
      more.hidden = true;
      grid.after(more);
      more.addEventListener('click', () => {
        const cards = [...grid.querySelectorAll(':scope > [data-fhg-card]')];
        const firstNew = cards.filter(card => card.dataset.fsMySizeHidden !== 'true')[visibleCount];
        visibleCount += pageSize;
        showCards();
        // Keep keyboard focus meaningful when the final reveal removes its button.
        if (more.hidden) firstNew?.querySelector('a')?.focus({ preventScroll: true });
      }, options);
    }
    enhanceCards();
    const gridObserver = grid && typeof window.MutationObserver === 'function' ? new window.MutationObserver(records => {
      if (records.some(record => record.type === 'childList' && record.target === grid)) enhanceCards();
    }) : null;
    gridObserver?.observe(grid, { childList: true });

    root.querySelector('[data-fsb-shop]')?.addEventListener('click', () => {
      const panel = root.querySelector('[data-fsic-panel]');
      if (panel?.hidden) {
        const category = root.querySelector('[data-fsic-category].is-active') || root.querySelector('[data-fsic-category="hoodies"]') || root.querySelector('[data-fsic-category]');
        category?.click();
      }
    }, options);

    const collectionNav = root.querySelector('[data-fsic-categories]');
    const collectionPhotos = [...root.querySelectorAll('[data-fsb-collection]')];
    const collectionPanel = root.querySelector('[data-fsic-panel]');
    const categoryFor = photo => [...(collectionNav?.querySelectorAll('[data-fsic-category]') || [])].find(link => link.dataset.fsicCategory === photo.dataset.fsbCollection);
    function revealCollectionPill(pill) {
      const rail = pill.closest('.fsb-collection-pills');
      if (!rail || rail.scrollWidth <= rail.clientWidth) return;
      const frame = rail.getBoundingClientRect();
      const item = pill.getBoundingClientRect();
      const delta = item.left < frame.left + 6 ? item.left - frame.left - 6 : item.right > frame.right - 6 ? item.right - frame.right + 6 : 0;
      // Move this horizontal rail only. Do not jump the document or intercept touch gestures.
      if (delta) rail.scrollBy({ left: delta, behavior: reduced.matches ? 'auto' : 'smooth' });
    }
    function syncCollectionPhotos() {
      collectionPhotos.forEach(photo => {
        const wasActive = photo.getAttribute('aria-current') === 'true';
        const active = !collectionPanel?.hidden && categoryFor(photo)?.classList.contains('is-active');
        if (active) photo.setAttribute('aria-current', 'true'); else photo.removeAttribute('aria-current');
        photo.setAttribute('aria-expanded', String(Boolean(active)));
        if (active && !wasActive) revealCollectionPill(photo);
      });
    }
    collectionPhotos.forEach(photo => {
      photo.setAttribute('role', 'button');
      photo.addEventListener('focus', () => revealCollectionPill(photo), options);
      if (collectionPanel?.id) photo.setAttribute('aria-controls', collectionPanel.id);
      photo.addEventListener('keydown', event => { if (event.key === ' ' && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !event.repeat) { event.preventDefault(); photo.click(); } }, options);
      photo.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        const category = categoryFor(photo);
        const controller = collectionNav?.closest('[data-fs-inline-categories]')?.fsic;
        if (!category || !collectionPanel || !controller) return;
        event.preventDefault();
        if (controller.grid?.pending) { controller.grid.status('cart_busy'); return; }
        // Reuse the existing category controller and its cart-pending guard.
        if (collectionPanel.hidden || !category.classList.contains('is-active')) category.click();
        if (!collectionPanel.hidden) {
          collectionPanel.scrollIntoView({ block: 'start', behavior: reduced.matches ? 'instant' : 'smooth' });
          const heading = collectionPanel.querySelector('[data-fsic-title]');
          heading?.setAttribute('tabindex', '-1');
          heading?.focus({ preventScroll: true });
        }
        syncCollectionPhotos();
      }, options);
    });
    const categoryObserver = collectionNav && typeof window.MutationObserver === 'function' ? new window.MutationObserver(syncCollectionPhotos) : null;
    categoryObserver?.observe(collectionNav, { subtree: true, attributes: true, attributeFilter: ['class'] });
    if (collectionPanel) categoryObserver?.observe(collectionPanel, { attributes: true, attributeFilter: ['hidden'] });
    syncCollectionPhotos();

    const revealObserver = !reduced.matches && 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fsb-reveal-in');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: .08 }) : null;
    root.querySelectorAll('[data-fsb-reveal]').forEach(node => revealObserver?.observe(node));
    const resetMotion = () => { if (reduced.matches) revealObserver?.disconnect(); };
    reduced.addEventListener('change', resetMotion, options);

    instances.set(root, () => {
      abort.abort();
      gridObserver?.disconnect();
      categoryObserver?.disconnect();
      revealObserver?.disconnect();
      more?.remove();
      grid?.querySelectorAll('[data-fhg-card]').forEach(card => { card.hidden = false; });
      instances.delete(root);
    });
  }
  function start(scope = document) { scope.querySelectorAll('[data-fsb-home]').forEach(mount); }
  start();
  document.addEventListener('shopify:section:load', event => start(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-fsb-home]').forEach(root => instances.get(root)?.()));
})();
